import { colors, icons, intl, ui, usePrevious } from '@beelday/common'
import { useAuthenticatedUser } from '@beelday/common/src/security'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'app-redux'
import { Config } from 'common/config'
import useMicApi from 'features/microphone/microphone-api'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReportMediaProgress } from 'room/common/media-progress'
import { StageControls, useStageSurface } from 'room/common/stage-surface'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useSessionMode } from 'session-mode/use-session-mode'
import TwoColumnsVideoGrid from 'room/training-room/ice-breaker/presentation/two-columns-video-grid'
import { SceneVideoUser } from 'video-conference-media'
import useYouTubeStreamingApi from './youtube-streaming-api'
import useYouTubeCommands from './use-youtube-commands'
import {
	YouTubeStreamOffsetRequested,
	YouTubeStreamUserReadiness,
	iRequestOffset,
	selectYouTubeStreamingOffsetRequested,
	selectYouTubeStreamingUserReadiness,
	selectYouTubeStreamState,
} from './youtube-streaming-redux'
import { YouTubeStreamState } from './model'
import {
	extractYouTubeVideoId,
	loadYouTubeIframeApi,
	YouTubeIframeApi,
	YouTubePlayer,
	YouTubePlayerEvent,
} from './youtube-utils'

type Props = {
	streams: Array<SceneVideoUser>
}

export const YouTubeStreamingTrainer = ({ streams }: Props): JSX.Element => {
	const youtubeStream = useSelector(selectYouTubeStreamState)
	const youtubeStreamOffsetRequested = useSelector(
		selectYouTubeStreamingOffsetRequested
	)
	const youtubeStreamUserReadiness = useSelector(
		selectYouTubeStreamingUserReadiness
	)

	return youtubeStream && youtubeStreamOffsetRequested && streams ? (
		<ConfiguredYouTubeStreamingTrainer
			youtubeStream={youtubeStream}
			youtubeStreamOffsetRequested={youtubeStreamOffsetRequested}
			audioUserReadiness={youtubeStreamUserReadiness}
			streams={streams}
		/>
	) : (
		<></>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: flex-start;
	min-height: 0;
	width: 100%;
	padding: 8px 24px 8px 24px;
	row-gap: 12px;
`

/**
 * Everything in the column above and below the picture: its padding, the
 * title row, the controls, a one-line description and the gaps between them.
 */
const IN_PERSON_CHROME_HEIGHT = '174px'
/** The column's side padding: the picture is this much narrower than it. */
const IN_PERSON_SIDE_PADDING = '48px'

/**
 * A size container, so the column inside can be as wide as the 16:9 picture
 * that fits the stage's height.
 */
const InPersonStage = styled.div`
	container-type: size;
	display: flex;
	justify-content: center;
	flex-grow: 1;
	min-height: 0;
	width: 100%;
	padding: 10px 0;
`

const InPersonContainer = styled(Container)`
	width: min(
		100%,
		(100cqh - ${IN_PERSON_CHROME_HEIGHT}) * 16 / 9 + ${IN_PERSON_SIDE_PADDING}
	);
`

/**
 * In person the projector shows the film alone - there are no cameras in the
 * room to put beside it - so the title, the picture and the controls line up
 * centred across the stage. Online the videos stay beside the film.
 */
const Stage: FC<{
	inPerson: boolean
	streams: SceneVideoUser[]
}> = ({ inPerson, streams, children }) =>
	inPerson ? (
		<InPersonStage>{children}</InPersonStage>
	) : (
		<TwoColumnsVideoGrid sceneVideoUsers={streams}>
			{children}
		</TwoColumnsVideoGrid>
	)

const Title = styled.div`
	font-size: 28px;
	line-height: 36px;
	font-weight: 700;
	align-self: flex-start;
	text-align: left;
	word-break: break-all;

	${ui.responsive.desktop1440} {
		font-size: 24px;
		line-height: 30px;
	}
`

const ControlsContainer = styled(ui.Flex)`
	column-gap: 20px;
	padding: 8px 10px;
	background-color: ${colors.lightPurple};
	width: 100%;
	border-radius: 50px;
	justify-content: space-between;
	align-items: center;
	max-width: 100%;
	align-self: center;
`

const ControlButton = styled.button`
	height: 40px;
	border-radius: 20px;
	border: 2px solid ${colors.borderLightGray};
	background-color: ${colors.indigoBlue};
	color: ${colors.white};
	padding: 0 14px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		opacity: 0.92;
	}
`

const Header = styled(ui.Flex)`
	width: 100%;
	justify-content: space-between;
	align-items: flex-start;
	column-gap: 20px;
`

const PlayerColumn = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	row-gap: 16px;
`

const PlayerStage = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
	min-height: 0;
	width: 100%;
`

const PlayerShell = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	aspect-ratio: 16 / 9;
	max-height: 100%;
	margin: 0;
`

/**
 * Above everything on the projector's stage, under its black screen
 * (`ProjectorCover` in `presenter-mode/`, 10000), which stays on top of the
 * film.
 */
const PROJECTOR_FILM_Z_INDEX = 9000

/**
 * The film's container on the projector: the whole presentation window, on
 * black, whatever the film's ratio - the frame inside, at 100%, shows it edge
 * to edge. Fixed to the window's edges, so no ancestor of it may be a size
 * container or transformed (it would become the containing block).
 */
const ProjectorFilm = styled(PlayerShell)`
	position: fixed;
	inset: 0;
	z-index: ${PROJECTOR_FILM_Z_INDEX};
	aspect-ratio: auto;
	max-height: none;
	background: ${colors.black};
`

const PlayerSurface = styled.div`
	width: 100%;
	height: 100%;

	> div,
	iframe {
		width: 100%;
		height: 100%;
	}
`

type ConfiguredProps = {
	youtubeStream: YouTubeStreamState
	youtubeStreamOffsetRequested: YouTubeStreamOffsetRequested
	audioUserReadiness: YouTubeStreamUserReadiness
	streams: Array<SceneVideoUser>
}

export const ConfiguredYouTubeStreamingTrainer = ({
	youtubeStream,
	youtubeStreamOffsetRequested,
	audioUserReadiness,
	streams,
}: ConfiguredProps): JSX.Element => {
	const dispatch = useDispatch()
	const youtubeStreamingApi = useYouTubeStreamingApi()
	const commands = useYouTubeCommands()
	const address = useAssertedJoinedRoomAddress()
	const microphoneApi = useMicApi(Config.beeldayBackendUrl)
	const assertedUser = useAuthenticatedUser()
	const inPerson = useSessionMode() === 'IN_PERSON'
	const onProjector = useStageSurface() === 'projector'
	const [currentTime, setCurrentTime] = useState(0)
	const [duration, setDuration] = useState(0)
	const [playerReady, setPlayerReady] = useState(false)
	const [isSeeking, setIsSeeking] = useState(false)
	const [seekTime, setSeekTime] = useState(0)
	const [isFullscreen, setIsFullscreen] = useState(false)

	const { previous: previousState, current: currentState } = usePrevious(
		youtubeStream?.state
	)

	const playerRef = useRef<YouTubePlayer | null>(null)
	const playerContainerRef = useRef<HTMLDivElement | null>(null)
	const playerShellRef = useRef<HTMLDivElement | null>(null)
	const seekCommitTimeoutRef = useRef<number | null>(null)
	const isScrubbingRef = useRef(false)
	const pendingSeekTimeRef = useRef<number | null>(null)
	const lastCommittedSeekTimeRef = useRef<number | null>(null)
	const ignoreNextPlayerStateChangeRef = useRef(false)
	const suppressPlayerStateSyncUntilRef = useRef(0)
	const appliedInitialPlayingOffsetRef = useRef(false)
	const youtubeStreamOffsetRef = useRef(youtubeStream.offset)
	const youtubeStreamStateRef = useRef(youtubeStream.state)

	const videoId = useMemo(
		() => extractYouTubeVideoId(youtubeStream.link),
		[youtubeStream.link]
	)

	const onPlayPause = () => {
		const currentTimeMs = Math.floor(
			(playerRef.current?.getCurrentTime() || 0) * 1000
		)

		if (youtubeStream.state === 'Playing') {
			commands.pause(currentTimeMs)
		} else {
			commands.play(currentTimeMs)
		}
	}

	const toggleFullscreen = useCallback(async () => {
		const target = playerShellRef.current as
			| (HTMLDivElement & {
					webkitRequestFullscreen?: () => Promise<void> | void
			  })
			| null
		const fullscreenDocument = document as Document & {
			webkitFullscreenElement?: Element | null
			exitFullscreen?: () => Promise<void>
			webkitExitFullscreen?: () => Promise<void> | void
		}

		if (!target) return

		if (
			document.fullscreenElement === target ||
			fullscreenDocument.webkitFullscreenElement === target
		) {
			if (document.exitFullscreen) {
				await document.exitFullscreen()
			} else if (fullscreenDocument.webkitExitFullscreen) {
				await fullscreenDocument.webkitExitFullscreen()
			}
			return
		}

		if (target.requestFullscreen) {
			await target.requestFullscreen()
		} else if (target.webkitRequestFullscreen) {
			await target.webkitRequestFullscreen()
		}
	}, [])

	const playVideo = useCallback(
		(startOffsetMs?: number) => {
			microphoneApi.muteMic(address, assertedUser.id).then(() => {
				if (audioUserReadiness.ready) {
					if (typeof startOffsetMs === 'number') {
						playerRef.current?.seekTo(startOffsetMs / 1000, true)
						setCurrentTime(startOffsetMs / 1000)
					}
					playerRef.current?.playVideo()
				}
			})
		},
		[address, assertedUser.id, audioUserReadiness.ready, microphoneApi]
	)
	useEffect(() => {
		youtubeStreamOffsetRef.current = youtubeStream.offset
	}, [youtubeStream.offset])

	useEffect(() => {
		youtubeStreamStateRef.current = youtubeStream.state
	}, [youtubeStream.state])

	useEffect(() => {
		const fullscreenDocument = document as Document & {
			webkitFullscreenElement?: Element | null
		}
		const handleFullscreenChange = () => {
			setIsFullscreen(
				document.fullscreenElement === playerShellRef.current ||
					fullscreenDocument.webkitFullscreenElement === playerShellRef.current
			)
		}

		document.addEventListener('fullscreenchange', handleFullscreenChange)
		document.addEventListener(
			'webkitfullscreenchange',
			handleFullscreenChange as EventListener
		)

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange)
			document.removeEventListener(
				'webkitfullscreenchange',
				handleFullscreenChange as EventListener
			)
		}
	}, [])

	useEffect(() => {
		let isActive = true
		setPlayerReady(false)

		if (!videoId || !playerContainerRef.current) return

		loadYouTubeIframeApi().then(api => {
			if (!isActive || !playerContainerRef.current) return
			const yt = api as YouTubeIframeApi
			playerRef.current = new yt.Player(playerContainerRef.current, {
				videoId,
				playerVars: {
					controls: 0,
					cc_load_policy: 0,
					disablekb: 1,
					fs: 1,
					iv_load_policy: 3,
					origin: window.location.origin,
					rel: 0,
					modestbranding: 1,
					playsinline: 1,
				},
				events: {
					onReady: () => {
						setPlayerReady(true)
						setDuration(playerRef.current?.getDuration?.() || 0)
						const iframe = playerRef.current?.getIframe?.()
						if (iframe) {
							iframe.setAttribute(
								'allow',
								'autoplay; encrypted-media; fullscreen'
							)
							iframe.setAttribute('allowfullscreen', 'true')
						}
						playerRef.current?.cueVideoById(
							videoId,
							(youtubeStreamOffsetRef.current || 0) / 1000
						)
					},
					onStateChange: (event: YouTubePlayerEvent) => {
						if (event.data === yt.PlayerState.ENDED) {
							commands.finish()
							return
						}

						if (Date.now() < suppressPlayerStateSyncUntilRef.current) {
							return
						}

						if (ignoreNextPlayerStateChangeRef.current) {
							ignoreNextPlayerStateChangeRef.current = false
							return
						}

						const currentTimeMs = Math.floor(
							(playerRef.current?.getCurrentTime?.() || 0) * 1000
						)
						const currentStreamState = youtubeStreamStateRef.current

						if (
							event.data === yt.PlayerState.PLAYING &&
							currentStreamState !== 'Playing'
						) {
							commands.play(currentTimeMs)
						} else if (
							event.data === yt.PlayerState.PAUSED &&
							currentStreamState !== 'Paused'
						) {
							commands.pause(currentTimeMs)
						}
					},
				},
			})
		})

		return () => {
			isActive = false
			if (playerRef.current?.destroy) {
				playerRef.current.destroy()
			}
			playerRef.current = null
		}
	}, [commands, videoId])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		const interval = setInterval(() => {
			const nextTime = playerRef.current?.getCurrentTime?.() || 0
			const nextDuration = playerRef.current?.getDuration?.() || 0
			if (!isSeeking) {
				setCurrentTime(nextTime)
			}
			setDuration(nextDuration)
		}, 200)

		return () => clearInterval(interval)
	}, [isSeeking, playerReady])

	useReportMediaProgress({
		kind: 'youtube',
		positionMs: Math.floor(currentTime * 1000),
		durationMs: Math.floor(duration * 1000),
		playing: youtubeStream.state === 'Playing',
	})

	const commitSeek = useCallback(
		(nextTime: number) =>
			commands.seek(
				Math.floor(nextTime * 1000),
				youtubeStream.state === 'Playing'
			),
		[commands, youtubeStream.state]
	)

	const clearPendingSeekCommit = useCallback(() => {
		if (seekCommitTimeoutRef.current !== null) {
			window.clearTimeout(seekCommitTimeoutRef.current)
			seekCommitTimeoutRef.current = null
		}
	}, [])

	const commitPendingSeek = useCallback(() => {
		seekCommitTimeoutRef.current = null

		if (pendingSeekTimeRef.current === null) return

		const nextTime = pendingSeekTimeRef.current

		suppressPlayerStateSyncUntilRef.current = Date.now() + 800
		playerRef.current?.seekTo(nextTime, true)
		setCurrentTime(nextTime)

		if (!isScrubbingRef.current) {
			setIsSeeking(false)
		}

		if (lastCommittedSeekTimeRef.current === nextTime) return

		lastCommittedSeekTimeRef.current = nextTime
		commitSeek(nextTime)
	}, [commitSeek])

	const scheduleSeekCommit = useCallback(() => {
		clearPendingSeekCommit()
		seekCommitTimeoutRef.current = window.setTimeout(() => {
			commitPendingSeek()
		}, 200)
	}, [clearPendingSeekCommit, commitPendingSeek])

	const finishSeekInteraction = useCallback(() => {
		if (!isScrubbingRef.current) return

		isScrubbingRef.current = false
		scheduleSeekCommit()
	}, [scheduleSeekCommit])

	useEffect(() => {
		return () => {
			clearPendingSeekCommit()
		}
	}, [clearPendingSeekCommit])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		if (isScrubbingRef.current) return
		playerRef.current.seekTo((youtubeStream?.offset || 0) / 1000, true)
	}, [playerReady, youtubeStream?.offset])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		if (!previousState && currentState === 'Playing') {
			const initialStartOffsetMs = appliedInitialPlayingOffsetRef.current
				? undefined
				: youtubeStreamOffsetRef.current + 1000

			if (
				!youtubeStreamOffsetRequested.request.meRequesting &&
				!audioUserReadiness.ready
			) {
				dispatch(iRequestOffset())
				microphoneApi.muteMic(address, assertedUser.id).then(() => {
					youtubeStreamingApi.requestOffset(address)
				})
			}

			if (audioUserReadiness.ready) {
				appliedInitialPlayingOffsetRef.current = true
				ignoreNextPlayerStateChangeRef.current = true
				playVideo(initialStartOffsetMs)
			}
		} else if (previousState === 'Stopped' && currentState === 'Playing') {
			playVideo()
		} else if (previousState === 'Paused' && currentState === 'Playing') {
			playVideo()
		} else if (currentState === 'Stopped') {
			ignoreNextPlayerStateChangeRef.current = true
			playerRef.current?.pauseVideo()
		} else if (currentState === 'Paused') {
			ignoreNextPlayerStateChangeRef.current = true
			playerRef.current?.pauseVideo()
		}
	}, [
		address,
		assertedUser.id,
		audioUserReadiness.ready,
		currentState,
		dispatch,
		microphoneApi,
		playerReady,
		playVideo,
		previousState,
		youtubeStreamOffsetRequested.request.meRequesting,
		youtubeStreamingApi,
	])

	useEffect(() => {
		if (
			youtubeStreamOffsetRequested.request.requested &&
			!youtubeStreamOffsetRequested.request.meRequesting &&
			!youtubeStreamOffsetRequested.answer.answered
		) {
			youtubeStreamingApi.provideOffset(
				address,
				Math.floor((playerRef.current?.getCurrentTime?.() || 0) * 1000)
			)
		}
	}, [address, youtubeStreamOffsetRequested, youtubeStreamingApi])

	// The projector shows the film and nothing else, from the moment the item
	// appears: no title, controls or description, and nothing of the stage
	// around it. The trainer works the film from the console's remote.
	if (onProjector) {
		return (
			<ProjectorFilm ref={playerShellRef} data-testid="youtube_stream.player">
				<PlayerSurface ref={playerContainerRef} />
			</ProjectorFilm>
		)
	}

	const Column = inPerson ? InPersonContainer : Container

	return (
		<Stage inPerson={inPerson} streams={streams}>
			<Column data-testid="youtube_stream">
				<Header>
					<Title>{youtubeStream.name}</Title>
					<StageControls>
						<ui.ButtonClose
							css={css`
								width: 36px;
								height: 36px;
								min-width: 36px;
							`}
							onClick={commands.close}
						/>
					</StageControls>
				</Header>
				<PlayerColumn>
					<PlayerStage>
						<PlayerShell
							ref={playerShellRef}
							data-testid="youtube_stream.player"
						>
							<PlayerSurface ref={playerContainerRef} />
						</PlayerShell>
					</PlayerStage>

					<StageControls>
						<ControlsContainer>
							<button
								css={css`
									min-width: 48px;
									min-height: 48px;
									border-radius: 50%;
									border: 4px solid ${colors.borderLightGray};
									background-color: ${youtubeStream.state === 'Playing'
										? colors.pinkRed
										: colors.indigoBlue};
									color: ${colors.white};
									display: flex;
									align-items: center;
									justify-content: center;
									cursor: pointer;
									transition: 0.3s ease-in-out;

									&:hover {
										scale: 1.1;
									}
								`}
								onClick={onPlayPause}
							>
								<div
									css={css`
										width: 20px;
										height: 20px;
										svg {
											height: 20px;
											width: 20px;
										}
									`}
								>
									{youtubeStream.state === 'Playing' ? (
										<icons.PauseIcon />
									) : (
										<icons.PlayIcon />
									)}
								</div>
							</button>
							<input
								min={0}
								max={duration || 0}
								step={0.1}
								value={isSeeking ? seekTime : currentTime}
								css={css`
									width: 100%;
									accent-color: ${colors.white};
								`}
								onChange={event => {
									const nextTime = Number(event.currentTarget.value)
									isScrubbingRef.current = true
									pendingSeekTimeRef.current = nextTime
									setIsSeeking(true)
									setSeekTime(nextTime)
									setCurrentTime(nextTime)
									scheduleSeekCommit()
								}}
								onMouseUp={() => {
									finishSeekInteraction()
								}}
								onTouchEnd={() => {
									finishSeekInteraction()
								}}
								onBlur={() => {
									finishSeekInteraction()
								}}
								type="range"
							/>
							<ui.H2 style={{ color: colors.white, minWidth: '140px' }}>
								{formatTime(currentTime)} / {formatTime(duration)}
							</ui.H2>
							<ControlButton onClick={toggleFullscreen} type="button">
								<intl.Translate>
									{isFullscreen
										? 'youtube_stream.controls.exit_fullscreen'
										: 'youtube_stream.controls.fullscreen'}
								</intl.Translate>
							</ControlButton>
						</ControlsContainer>
					</StageControls>
				</PlayerColumn>

				<ui.H5
					style={{
						maxWidth: '100%',
						wordBreak: 'break-all',
					}}
				>
					{youtubeStream.description}
				</ui.H5>
			</Column>
		</Stage>
	)
}

const formatTime = (time: number): string => {
	const minutes = Math.floor(time / 60)
	const seconds = Math.floor(time % 60)
		.toString()
		.padStart(2, '0')
	return `${minutes}:${seconds}`
}
